import { useMutation } from '@apollo/client'

import { BaseStatusMenu } from '../../../../../components/base-status-menu'
import { ShipmentStatusLabel } from '../../../../../components/shipment-status-label'
import { UPDATE_SHIPMENT_STATUS } from '../../../../../data/mutations/shipment'
import { ShipmentStatusEnum } from '../../../../../types/shipment'

interface TruckloadStatusMenuProps {
  currentStatus: ShipmentStatusEnum
  isShipmentOwner: boolean
  shipmentID: string
}

/**
 * Returns the available statuses for a shipment
 * @param currentStatus - The current status of the shipment
 * @returns The available statuses for a shipment
 */
const getAvailableStatuses = (currentStatus: ShipmentStatusEnum) => {
  const baseStatuses = [
    ShipmentStatusEnum.NeedsCarrier,
    ShipmentStatusEnum.CarrierAssigned,
    ShipmentStatusEnum.InTransit,
    ShipmentStatusEnum.Delivered,
    ShipmentStatusEnum.Canceled
  ]

  // Only include expired status in the dropdown if the current status is expired
  const availableStatuses =
    currentStatus === ShipmentStatusEnum.Expired ? [...baseStatuses, ShipmentStatusEnum.Expired] : baseStatuses

  return availableStatuses
}

/**
 * This component is used to display the status of a shipment.
 * It displays the current status of the shipment and allows the user to change the status.
 */
export const ShipmentStatusMenu = (props: TruckloadStatusMenuProps) => {
  const { currentStatus, shipmentID, isShipmentOwner } = props

  const [updateShipmentStatus] = useMutation(UPDATE_SHIPMENT_STATUS, {
    onError: (error) => {
      console.error('Failed to update shipment status:', error)
    }
  })

  const handleUpdateStatus = ({ status }: { status: string }) => {
    updateShipmentStatus({
      variables: {
        id: shipmentID,
        shipmentStatus: status
      }
    })
  }

  const availableStatuses = getAvailableStatuses(currentStatus)

  return (
    <BaseStatusMenu
      canEdit={isShipmentOwner}
      currentStatus={currentStatus}
      handleUpdateStatus={handleUpdateStatus}
      labelComponent={(status: string) => <ShipmentStatusLabel status={status as ShipmentStatusEnum} />}
      statuses={availableStatuses}
      disabledStatuses={currentStatus === ShipmentStatusEnum.Expired ? [ShipmentStatusEnum.Expired] : []}
    />
  )
}
